import { deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';

/**
 * Carry a caller's edits onto the singleton document the database is about to save.
 *
 * `savePlanAsync` and `saveFitnessStateAsync` save whichever object the database
 * currently holds for a singleton id, not the object their caller edited. A caller
 * that edits through a `CouchDocDraft` captured its object when its page loaded, and
 * the database can be re-opened under an open page — after which the two are different
 * objects and the user's edits go nowhere. This copies the committed content onto the
 * live object so the save persists what the user actually typed.
 *
 * `deepOverwriteCouchSafe` preserves the destination's `_id`, `_rev` and `db__*`, so
 * the live singleton keeps the revision the database expects and the save is not turned
 * into a conflict.
 *
 * Why copying rather than refusing, why it cannot write one account's data into
 * another's, and which situation re-opens the database at all:
 * `design-docs/sync-integrity.md`, "A third door" (df-224ad165d843).
 *
 * @param live the singleton document the database will save
 * @param committed the object the caller edited and committed, or undefined when the
 *   caller does not track one (every existing caller but the Edit Workouts page)
 * @returns true when the two were different objects and the edits had to be carried
 *   across — the caller reports that, because it is a condition nothing else can see.
 */
export function carryCommittedEditsOntoSaveTarget<T extends object>(
  live: T | undefined,
  committed: T | undefined,
): boolean {
  // nothing to carry: the caller tracks no committed object, or it is the very object
  // the database holds, which is the healthy case and by far the common one
  if (committed == null || live == null || committed === live) {
    return false;
  }
  deepOverwriteCouchSafe(live, committed);
  return true;
}
